import type { Profile, WeightEntry } from '../lib/metrics'
import type { ActivityKind, DayOverride, TemplateDay } from '../lib/schedule'
import type { CatalogExercise, ProgramEdits } from '../lib/program'
import type { LastLog, Session, toUpsert } from '../lib/session'
import type { Measurement } from '../lib/body'
import type { ActivityEntry } from '../lib/energy'

export interface Me {
  id: number
  email: string
  name: string
  isAdmin: boolean
  timeZone: string
  profile: Profile
}

export interface UserRow {
  id: number
  email: string
  name: string
  isAdmin: boolean
  createdAt: string
  lastLoginAt: string
}

export interface EquipmentSettings {
  trainingPlace: string
  equipment: string[]
  picks: Record<string, string> | null
  /** Gün bazlı elle düzenlemeler — üretilen programın üstüne uygulanan fark. */
  edits: ProgramEdits | null
  /** Seçilen bölünme: full_ab | upper_lower | ppl. */
  split: string | null
  /**
   * Elindeki direnç bantlarının kg değerleri, ör. [15, 30].
   * Boşsa bant hareketlerinde kg kutusu açılmıyor — bkz. isLoadable.
   */
  bandsKg: number[]
}

export interface StoredActivity {
  id: number
  date: string
  activityKey: string
  minutes: number
  kcalOverride: number | null
  note: string | null
}

export interface DailyLog {
  date: string
  checks: boolean[]
  cycles: number
  standSeconds: number
  strengthDone: boolean
  updatedAt: string
}

export interface ScheduleDto {
  template: TemplateDay[]
  overrides: DayOverride[]
  strengthDoneDates: string[]
}

/** Yönetici kullanım özeti — bkz. UsageEndpoints.cs. */
export interface UsageRow {
  userId: number
  email: string
  name: string
  isAdmin: boolean
  createdAt: string
  lastLoginAt: string
  /** Son 30 günde KAYIT bırakılan farklı gün sayısı (giriş sayısı değil). */
  activeDays30: number
  workouts30: number
  workoutsTotal: number
  weights30: number
  weightsTotal: number
  activities30: number
  activitiesTotal: number
  lastEntry: string | null
  startWeightKg: number
  targetWeightKg: number
  /** En son tartı; hiç tartılmadıysa null. */
  currentWeightKg: number | null
  goal: string
}

export interface UsageEvent {
  date: string
  name: string
  kind: 'workout' | 'weight' | 'activity'
  text: string
}

export interface UsageDto {
  users: UsageRow[]
  feed: UsageEvent[]
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
    ...init,
  })

  if (res.status === 401) throw new ApiError(401, 'Oturum yok')
  if (!res.ok) throw new ApiError(res.status, (await res.text()) || res.statusText)
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export const api = {
  login: (email: string, password: string) =>
    req<{ id: number; email: string; name: string; isAdmin: boolean }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => req<void>('/auth/logout', { method: 'POST' }),

  me: () => req<Me>('/api/me'),

  // Kullanıcı yönetimi — sadece yönetici hesapları
  listUsers: () => req<UserRow[]>('/api/admin/users'),
  usage: () => req<UsageDto>('/api/admin/usage'),
  // password null → sunucu güçlü bir parola üretip BİR KEZ geri döner.
  // Dolu gönderilirse o kullanılır (en az 8 karakter, sunucu da kontrol ediyor).
  createUser: (email: string, name: string, isAdmin: boolean, password: string | null = null) =>
    req<{ id: number; email: string; name: string; isAdmin: boolean; password: string }>(
      '/api/admin/users',
      { method: 'POST', body: JSON.stringify({ email, name, password, isAdmin }) }),
  resetPassword: (id: number, password: string | null = null) =>
    req<{ id: number; password: string }>(`/api/admin/users/${id}/password`,
      { method: 'POST', body: JSON.stringify({ password }) }),
  deleteUser: (id: number) => req<void>(`/api/admin/users/${id}`, { method: 'DELETE' }),
  saveProfile: (p: Profile) =>
    req<Profile>('/api/profile', { method: 'PUT', body: JSON.stringify(p) }),

  weights: (from?: string, to?: string) => {
    const q = new URLSearchParams()
    if (from) q.set('from', from)
    if (to) q.set('to', to)
    return req<WeightEntry[]>(`/api/weights${q.toString() ? '?' + q : ''}`)
  },
  // PUT + tarih: idempotent. Ağ koptu ve istemci tekrar denedi → çift kayıt oluşmuyor.
  saveWeight: (date: string, kg: number, note?: string | null, updatedAt?: string) =>
    req<WeightEntry>(`/api/weights/${date}`, {
      method: 'PUT',
      body: JSON.stringify({ kg, note: note ?? null, updatedAt: updatedAt ?? null }),
    }),
  deleteWeight: (date: string) => req<void>(`/api/weights/${date}`, { method: 'DELETE' }),

  logs: (from?: string, to?: string) => {
    const q = new URLSearchParams()
    if (from) q.set('from', from)
    if (to) q.set('to', to)
    return req<DailyLog[]>(`/api/logs${q.toString() ? '?' + q : ''}`)
  },
  saveLog: (date: string, body: Omit<DailyLog, 'date'>) =>
    req<DailyLog>(`/api/logs/${date}`, { method: 'PUT', body: JSON.stringify(body) }),

  exercises: () => req<CatalogExercise[]>('/api/exercises'),
  /**
   * Katalogun tamamı — küratörlü olmayanlar dâhil. Sadece "hareket ekle"
   * ekranında tüm katalog açıldığında çekiliyor; program üretici bunu
   * kullanmıyor ve varsayılan yükte istemciye inmiyor.
   */
  exercisesAll: () => req<CatalogExercise[]>('/api/exercises?all=true'),
  equipment: () => req<EquipmentSettings>('/api/equipment'),
  saveEquipment: (
    trainingPlace: string, equipment: string[],
    picks: Record<string, string>, edits: ProgramEdits = {}, split = 'full_ab',
    bandsKg: number[] = [],
  ) =>
    req<EquipmentSettings>('/api/equipment', {
      method: 'PUT',
      body: JSON.stringify({ trainingPlace, equipment, picks, edits, split, bandsKg }),
    }),

  // Antrenman kayıtları. Gün başına tek kayıt → PUT idempotent.
  sessions: (from?: string, to?: string) => {
    const q = new URLSearchParams()
    if (from) q.set('from', from)
    if (to) q.set('to', to)
    return req<Session[]>(`/api/sessions${q.toString() ? '?' + q : ''}`)
  },
  lastLogs: () => req<LastLog[]>('/api/sessions/last'),
  saveSession: (date: string, body: ReturnType<typeof toUpsert>) =>
    req<Session>(`/api/sessions/${date}`, { method: 'PUT', body: JSON.stringify(body) }),

  /**
   * Sayfa kapanırken/arka plana giderken son hâli yollar.
   *
   * Normal kayıt 700 ms bekliyor (her tuşta istek atmamak için). Telefonda bu
   * pencere gerçek bir risk: son seti işaretleyip ekranı kilitleyen biri o 700
   * ms'i doldurmadan gidiyor ve iOS sayfayı donduruyor — set kayboluyor.
   *
   * `keepalive` bu isteğin sayfa ölse bile tamamlanmasını sağlıyor. Cevabı
   * beklemiyoruz; zaten okuyacak bir sayfa kalmayabilir.
   */
  saveSessionBeacon: (date: string, body: ReturnType<typeof toUpsert>) => {
    try {
      void fetch(`/api/sessions/${date}`, {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: true,
      })
    } catch { /* kapanırken hata yutulur — gösterecek ekran yok */ }
  },
  deleteSession: (date: string) => req<void>(`/api/sessions/${date}`, { method: 'DELETE' }),

  // Çevre ölçümleri — gün başına tek kayıt, PUT idempotent.
  measurements: () => req<Measurement[]>('/api/measurements'),
  saveMeasurement: (date: string, m: Omit<Measurement, 'date'> & { updatedAt?: string }) =>
    req<Measurement>(`/api/measurements/${date}`, { method: 'PUT', body: JSON.stringify(m) }),
  deleteMeasurement: (date: string) =>
    req<void>(`/api/measurements/${date}`, { method: 'DELETE' }),

  // Günlük aktivite kaydı — aktivite çarpanının yerini alan şey.
  activities: (from?: string, to?: string) => {
    const q = new URLSearchParams()
    if (from) q.set('from', from)
    if (to) q.set('to', to)
    return req<StoredActivity[]>(`/api/activities${q.toString() ? '?' + q : ''}`)
  },
  addActivity: (a: ActivityEntry) =>
    req<StoredActivity>('/api/activities', { method: 'POST', body: JSON.stringify(a) }),
  deleteActivity: (id: number) => req<void>(`/api/activities/${id}`, { method: 'DELETE' }),

  adjustEnergy: (deltaKcal: number) =>
    req<{ calorieAdjust: number; consecutiveCuts: number }>('/api/energy/adjust', {
      method: 'POST', body: JSON.stringify({ deltaKcal }),
    }),
  resetEnergy: () =>
    req<{ calorieAdjust: number; consecutiveCuts: number }>('/api/energy/reset', { method: 'POST' }),

  schedule: () => req<ScheduleDto>('/api/schedule'),
  saveTemplate: (template: ScheduleDto['template']) =>
    req<unknown>('/api/schedule/template', { method: 'PUT', body: JSON.stringify(template) }),
  setOverride: (
    date: string, kind: ActivityKind, startTime: string | null,
    opts?: { activityKey?: string | null; minutes?: number | null; note?: string | null },
  ) =>
    req<unknown>(`/api/schedule/override/${date}`, {
      method: 'PUT',
      body: JSON.stringify({
        kind, startTime,
        activityKey: opts?.activityKey ?? null,
        minutes: opts?.minutes ?? null,
        note: opts?.note ?? null,
      }),
    }),
  clearOverride: (date: string) =>
    req<void>(`/api/schedule/override/${date}`, { method: 'DELETE' }),

  pushKey: () => req<{ publicKey: string }>('/api/push/key'),
  pushSubscribe: (endpoint: string, p256dh: string, auth: string, label?: string) =>
    req<void>('/api/push/subscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint, keys: { p256dh, auth }, label }),
    }),
  // POST — Minimal API gövdeli DELETE'i kabul etmiyor
  pushUnsubscribe: (endpoint: string) =>
    req<void>('/api/push/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint }) }),
  pushTest: () => req<{ sent: number }>('/api/push/test', { method: 'POST' }),

  exportAll: () => req<unknown>('/api/export'),
  importAll: (data: unknown) =>
    req<{ added: number; updated: number }>('/api/import', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
}
